import { Clock, Eye, MessageSquare, RefreshCw, Star, TrendingUp, Users } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api } from '@/lib/api'

interface AnalyticsSummary {
  totalViews: number
  uniqueViews: number
  totalWatchTime: number
  averageWatchTime: number
  completionRate: number
  dailyTrends: Array<{ date: string; views: number; watchTime: number }>
  rating: {
    average: number
    count: number
    distribution: Record<number, number>
  }
}

interface FeedbackItem {
  feedback: {
    id: string
    rating: number
    comment: string | null
    createdAt: string
  }
  userName: string | null
}

interface LessonAnalyticsDashboardProps {
  lessonId: string
  lessonTitle: string
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function LessonAnalyticsDashboard({ lessonId, lessonTitle }: LessonAnalyticsDashboardProps) {
  const [data, setData] = useState<AnalyticsSummary | null>(null)
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('overview')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [summary, feedbackResult] = await Promise.all([
        api.get<AnalyticsSummary>(`/api/lessons/${lessonId}/analytics/summary`),
        api.get<FeedbackItem[]>(`/api/lessons/${lessonId}/feedback`, { limit: '100' }),
      ])
      setData(summary)
      setFeedbacks(Array.isArray(feedbackResult) ? feedbackResult : [])
    } catch {
      setError('分析データの読み込みに失敗しました')
    } finally {
      setLoading(false)
    }
  }, [lessonId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">読み込み中...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={fetchData}>再試行</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">レッスン分析</h1>
          <p className="text-muted-foreground">{lessonTitle}</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          更新
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">概要</TabsTrigger>
          <TabsTrigger value="feedback">フィードバック</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">総視聴回数</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data?.totalViews ?? 0}</div>
                <p className="text-xs text-muted-foreground">
                  ユニーク視聴者: {data?.uniqueViews ?? 0}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">総視聴時間</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatTime(data?.totalWatchTime ?? 0)}</div>
                <p className="text-xs text-muted-foreground">
                  平均: {formatTime(data?.averageWatchTime ?? 0)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">完了率</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(data?.completionRate ?? 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  ユニーク視聴者: {data?.uniqueViews ?? 0}人
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">平均評価</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data?.rating.average.toFixed(1) ?? '---'}</div>
                <p className="text-xs text-muted-foreground">
                  フィードバック数: {data?.rating.count ?? 0}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Daily Trends Chart */}
          <Card>
            <CardHeader>
              <CardTitle>日別視聴トレンド</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={data?.dailyTrends ?? []}
                  margin={{ top: 20, right: 60, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    stroke="#666"
                    fontSize={12}
                    tickFormatter={(value: string) => {
                      const date = new Date(value)
                      return `${date.getMonth() + 1}/${date.getDate()}`
                    }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#3b82f6"
                    fontSize={12}
                    tickFormatter={(value: number) => value.toLocaleString()}
                    label={{
                      value: '視聴数',
                      angle: -90,
                      position: 'insideLeft',
                      offset: -5,
                      fontSize: 12,
                      fill: '#3b82f6',
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#10b981"
                    fontSize={12}
                    tickFormatter={(value: number) => formatTime(value)}
                    label={{
                      value: '視聴時間',
                      angle: 90,
                      position: 'insideRight',
                      offset: 10,
                      fontSize: 12,
                      fill: '#10b981',
                    }}
                  />
                  <Tooltip
                    labelFormatter={(value: string) => {
                      const date = new Date(value)
                      return date.toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        weekday: 'short',
                      })
                    }}
                    formatter={(value: number, name: string) => [
                      name === 'views'
                        ? `${value?.toLocaleString() ?? 0}回`
                        : formatTime(value ?? 0),
                      name === 'views' ? '視聴数' : '視聴時間',
                    ]}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="views"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="watchTime"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                  <span className="text-sm text-gray-600">視聴数</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <span className="text-sm text-gray-600">視聴時間</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback" className="space-y-6">
          {/* Rating Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>評価分布</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = data?.rating.distribution[rating] ?? 0
                  const total = data?.rating.count || 1
                  const percentage = (count / total) * 100

                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <div className="flex items-center gap-1 w-16">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm">{rating}</span>
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-yellow-400 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-12">{count}</span>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Feedback Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                フィードバックコメント ({feedbacks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {feedbacks.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  コメントはまだありません
                </p>
              ) : (
                <div className="space-y-4">
                  {feedbacks.map((item) => (
                    <div key={item.feedback.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">
                          {item.userName ?? '匿名ユーザー'}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={`star-${item.feedback.id}-${star}`}
                                className={`h-3 w-3 ${
                                  star <= item.feedback.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(item.feedback.createdAt).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                      </div>
                      {item.feedback.comment && (
                        <p className="text-sm text-muted-foreground">{item.feedback.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Insights */}
          <Card>
            <CardHeader>
              <CardTitle>分析インサイト</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data && data.completionRate < 0.5 && (
                  <div className="flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-500 mt-0.5" />
                    <p className="text-sm">
                      完了率が50%未満です。コンテンツの改善やエンゲージメント向上を検討してください。
                    </p>
                  </div>
                )}
                {data && data.rating.average > 0 && data.rating.average < 3 && (
                  <div className="flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-orange-500 mt-0.5" />
                    <p className="text-sm">
                      平均評価が3未満です。フィードバック内容を確認してください。
                    </p>
                  </div>
                )}
                {data &&
                  data.completionRate >= 0.5 &&
                  (data.rating.average >= 3 || data.rating.count === 0) && (
                    <div className="flex items-start gap-2">
                      <TrendingUp className="h-4 w-4 text-green-500 mt-0.5" />
                      <p className="text-sm">このレッスンは良好なパフォーマンスです。</p>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
